// cat > /mnt/user-data/outputs/Jammu.js << 'ENDOFFILE'
import React, { useState, useEffect, useRef, useCallback } from "react";

// ── Index configs ─────────────────────────────────────────────
const INDICES = {
  nifty:    { id: 13,  seg: "IDX_I", label: "NIFTY",    range: 500,  lotSize: 65,  futSeg: "NSE_FNO"  },
  sensex:   { id: 51,  seg: "IDX_I", label: "SENSEX",   range: 1000, lotSize: 20,  futSeg: "BSE_FNO"  },
  banknifty:{ id: 25,  seg: "IDX_I", label: "BANKNIFTY",range: 500,  lotSize: 30,  futSeg: "NSE_FNO"  },
  bankex:   { id: 69, seg: "IDX_I", label: "BANKEX",   range: 500,  lotSize: 30,  futSeg: "BSE_FNO"  },
};

const DEFAULT_REFRESH_SEC = 5;
const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";

// ── Styles ────────────────────────────────────────────────────
const C = {
  bg:     "#020812",
  panel:  "#050e1a",
  card:   "#070f1c",
  border: "#0c1f38",
  neon:   "#00ffe7",
  green:  "#00ff88",
  red:    "#ff3366",
  amber:  "#ffaa00",
  blue:   "#38bdf8",
  purple: "#a78bfa",
  pink:   "#f472b6",
  text:   "#b0c4de",
  muted:  "#2a3f5f",
  dim:    "#0f1e30",
};

const glowStyle = (color) => ({
  textShadow: `0 0 10px ${color}, 0 0 20px ${color}, 0 0 40px ${color}`,
  color,
});

const cardStyle = {
  background: C.card,
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  padding: "14px 16px",
};

const labelStyle = {
  fontSize: 9,
  color: C.muted,
  letterSpacing: 3,
  textTransform: "uppercase",
  marginBottom: 4,
};

const FONT = "'JetBrains Mono','Fira Code','Courier New',monospace";

// ── Safe fetch helper ─────────────────────────────────────────
async function sf(url, body) {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok || json.error) {
    throw new Error(json.error || `Request failed ${res.status}`);
  }

  return json;
}

// ── Main Component ────────────────────────────────────────────
export default function Jammu({ refreshSec = DEFAULT_REFRESH_SEC }) {

  const liveRefreshSec = Math.max(1, Number(refreshSec || DEFAULT_REFRESH_SEC));
  const userStoragePrefix = "secret_expiry_jammu";
  const storageKey = (key) => `${userStoragePrefix}_${key}`;

  const [spots, setSpots] = useState({});
  const [lastUpdated, setLastUpdated] = useState("");

  // Engine
  const [running,   setRunning]   = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [countdown, setCountdown] = useState(DEFAULT_REFRESH_SEC);
  const [error,     setError]     = useState("");
  const [data,      setData]      = useState(null);
  const [rangeData, setRangeData] = useState(null);
  const [activeJammuTab, setActiveJammuTab] = useState("main");
  const [greeksData, setGreeksData] = useState(null);
  const [greeksLoading, setGreeksLoading] = useState(false);
  const [greeksError, setGreeksError] = useState("");
  const greeksRef = useRef(null);
  const ivRef = useRef(null);
  const cdRef = useRef(null);

  // ── Fetch all data ──────────────────────────────────────────
    const fetchAll = useCallback(async () => {
      setLoading(true);
      setError("");

      try {
        const rangeRes = await sf(`${API_BASE}/api/secret-expiry/jammu/range`, {});
        setRangeData(rangeRes);

        const nextSpots = {};
        Object.keys(INDICES).forEach(key => {
          nextSpots[key] = rangeRes?.ranges?.[key]?.spot || "";
        });
        nextSpots.indiaVix = rangeRes?.indiaVix || rangeRes?.ranges?.nifty?.indiaVix || "";
        setSpots(nextSpots);

        const result = {};

        for (const [key, idx] of Object.entries(INDICES)) {
          const spot = Number(rangeRes?.ranges?.[key]?.spot || 0);

          if (!spot) {
            result[key] = null;
            continue;
          }

          try {
            const expData = await sf(`${API_BASE}/api/secret-expiry/options/expiry`, {
              index: key,
              underlyingId: idx.id,
              segment: idx.seg,
            });

            if (!expData.expiry) {
              result[key] = null;
              continue;
            }

            result[key] = await sf(`${API_BASE}/api/secret-expiry/jammu/chain`, {
              index: key,
              underlyingId: idx.id,
              segment: idx.seg,
              expiry: expData.expiry,
              spotPrice: spot,
              range: idx.range,
              allExpiries: expData.allExpiries || [],
            });
          } catch (e) {
            result[key] = { error: e.message };
          }
        }

        setData(result);
        setCountdown(liveRefreshSec);
        setLastUpdated(new Date().toISOString());
      } catch (e) {
        setError(e.message);
      }

      setLoading(false);
    }, [liveRefreshSec]);

  const start = async () => {
    setRunning(true);
    localStorage.setItem(storageKey("jammu_running"), "true");
    await fetchAll();
    ivRef.current = setInterval(fetchAll, liveRefreshSec * 1000);
    cdRef.current = setInterval(() =>
      setCountdown(c => c <= 1 ? liveRefreshSec : c - 1)
    , 1000);
  };

  const stop = () => {
    clearInterval(ivRef.current); clearInterval(cdRef.current);
    clearInterval(greeksRef.current);
    greeksRef.current = null;
    localStorage.removeItem(storageKey("jammu_running"));
    ivRef.current = null; cdRef.current = null;
    setRunning(false); setCountdown(liveRefreshSec);
  };

  const fetchGreeks = useCallback(async () => {
  setGreeksLoading(true);
  setGreeksError("");

  try {
    const liveRange = await sf(`${API_BASE}/api/secret-expiry/jammu/range`, {});
    setRangeData(liveRange);

    const out = {};

    for (const [key, idx] of Object.entries(INDICES)) {
      const liveSpot = Number(liveRange?.ranges?.[key]?.spot || spots[key] || 0);
      if (!liveSpot) continue;

      const expData = await sf(`${API_BASE}/api/secret-expiry/options/expiry`, {
        index: key,
        underlyingId: idx.id,
        segment: idx.seg,
      });

      if (!expData.expiry) continue;

      out[key] = await sf(`${API_BASE}/api/secret-expiry/jammu/chain`, {
        index: key,
        underlyingId: idx.id,
        segment: idx.seg,
        expiry: expData.expiry,
        spotPrice: liveSpot,
        range: idx.range,
        allExpiries: expData.allExpiries || [],
      });
    }

    setGreeksData(out);
  } catch (e) {
    setGreeksError(e.message || "Failed to fetch Greeks");
  } finally {
    setGreeksLoading(false);
  }
}, [spots]);

  const startGreeks = async () => {
  await fetchGreeks();
  clearInterval(greeksRef.current);
  greeksRef.current = setInterval(fetchGreeks, liveRefreshSec * 1000);
};

const stopGreeks = () => {
  clearInterval(greeksRef.current);
  greeksRef.current = null;
};

  useEffect(() => () => { clearInterval(ivRef.current); clearInterval(cdRef.current); }, []);

  // ── Signal computation ────────────────────────────────────────
  const signal = React.useMemo(() => {
    if (!data) return null;
    let bullish = 0, bearish = 0;

    for (const key of ["nifty", "sensex", "banknifty"]) {
      const d = data[key];
      if (!d || d.error) continue;
      // ITM Volume: more CE vol = bullish, more PE vol = bearish
      if (d.itm?.ceVolume > d.itm?.peVolume) bullish++;
      else if (d.itm?.peVolume > d.itm?.ceVolume) bearish++;
      // OI: more put sellers = bullish, more call sellers = bearish
      if (d.oi?.totalPeOI > d.oi?.totalCeOI) bullish++;
      else if (d.oi?.totalCeOI > d.oi?.totalPeOI) bearish++;
      // PCR
      const pcr = d.oi?.pcr || 0;
      if (pcr > 1.2) bullish++;
      else if (pcr < 0.8) bearish++;
      // Futures: buyers > sellers = bullish
      if (d.futures?.length > 0) {
        const f = d.futures[0];
        if (f.buyers > f.sellers) bullish++;
        else if (f.sellers > f.buyers) bearish++;
      }
    }

    if (bullish > bearish + 1) return { label: "BUY", color: C.green, sub: "BULLISH" };
    if (bearish > bullish + 1) return { label: "SELL", color: C.red, sub: "BEARISH" };
    return { label: "NEUTRAL", color: C.amber, sub: "WAIT" };
  }, [data]);

  const fmtOI  = v => v != null ? Number(v).toLocaleString("en-IN") : "—";
  const fmtN   = (v, d=2) => v != null ? Number(v).toFixed(d) : "—";
  const fmtTime = value =>
    value
      ? new Date(value).toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })
      : "";
  
  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text, fontFamily: FONT, padding: "0 0 40px 0" }}>

      {/* ── TOP BAR ── */}
      <div style={{ background: C.panel, borderBottom: `1px solid ${C.border}`, padding: "0 24px", display: "flex", justifyContent: "space-between", alignItems: "stretch", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "12px 0" }}>
          <span style={{ ...glowStyle(C.neon), fontSize: 16, fontWeight: "bold", letterSpacing: 4 }}>⬡ JAMMU SCANNER</span>
          <span style={{ fontSize: 8, color: C.muted, letterSpacing: 3 }}>INSTITUTIONAL MARKET INTELLIGENCE</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0" }}>
          {running && <span style={{ fontSize: 10, color: C.green, letterSpacing: 2 }}>● LIVE — {countdown}s</span>}
          {loading  && <span style={{ fontSize: 10, color: C.amber }}>⟳ FETCHING...</span>}
          {error    && <span style={{ fontSize: 10, color: C.red }}>⚠ {error}</span>}
        </div>
      </div>

      <div style={{ padding: "20px 24px", maxWidth: 1400, margin: "0 auto" }}>

        <div style={{ ...cardStyle, marginBottom: 20 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(120px, 1fr)) auto", gap: 12, alignItems: "end" }}>
            {Object.entries(INDICES).map(([key, idx]) => (
              <div key={key}>
                <div style={labelStyle}>{idx.label} SPOT</div>
                <div style={{ color: C.blue, fontSize: 15, fontWeight: "bold" }}>
                  {spots[key] || "-"}
                </div>
              </div>
            ))}

            <div style={{ textAlign: "right" }}>
              <div style={labelStyle}>LIVE REFRESH</div>
              <div style={{ color: C.green, fontSize: 12 }}>
                {liveRefreshSec}s {lastUpdated ? `| ${fmtTime(lastUpdated)}` : ""}
              </div>
            </div>
          </div>
        </div>

        {/* ── START / STOP ── */}
        <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 24 }}>
          {!running
            ? <button onClick={start} disabled={loading}
                style={{ background: "linear-gradient(135deg,#005c2e,#00ff88)", border: "none", color: "#000", padding: "12px 40px", borderRadius: 6, cursor: "pointer", fontFamily: FONT, fontSize: 14, fontWeight: "bold", letterSpacing: 3, boxShadow: `0 0 20px ${C.green}60` }}>
                ▶ START ALL SCANNERS
              </button>
            : <button onClick={stop}
                style={{ background: "linear-gradient(135deg,#5c0015,#ff3366)", border: "none", color: "#fff", padding: "12px 40px", borderRadius: 6, cursor: "pointer", fontFamily: FONT, fontSize: 14, fontWeight: "bold", letterSpacing: 3, boxShadow: `0 0 20px ${C.red}60` }}>
                ■ STOP
              </button>
          }
          
          <button
  onClick={fetchAll}
  disabled={loading}
  style={{
    background: "linear-gradient(135deg,#0f2040,#38bdf8)",
    border: "none",
    color: "#fff",
    padding: "12px 28px",
    borderRadius: 6,
    cursor: loading ? "not-allowed" : "pointer",
    fontFamily: FONT,
    fontSize: 13,
    fontWeight: "bold",
    letterSpacing: 2,
    boxShadow: `0 0 14px ${C.blue}50`,
    opacity: loading ? 0.6 : 1,
  }}
>
  REFRESH NOW
</button>


          {running && <div style={{ fontSize: 10, color: C.muted, letterSpacing: 2 }}>AUTO REFRESH EVERY {liveRefreshSec}s</div>}
        </div>

        {signal && (
          <div style={{ ...cardStyle, borderColor: signal.color + "60", marginBottom: 24, textAlign: "center", padding: "28px 20px" }}>
            <div style={{ fontSize: 10, color: C.muted, letterSpacing: 4, marginBottom: 8 }}>AGGREGATE MARKET SIGNAL</div>
            <div style={{ ...glowStyle(signal.color), fontSize: 72, fontWeight: "bold", lineHeight: 1, letterSpacing: 8 }}>
              {signal.label}
            </div>
            <div style={{ fontSize: 18, color: signal.color, letterSpacing: 6, marginTop: 8 }}>{signal.sub}</div>
          </div>
        )}

        {!data && !loading && (
          <div style={{ textAlign: "center", padding: "80px 0", color: C.muted }}>
            <div style={{ fontSize: 56, marginBottom: 16, color: C.neon, opacity: 0.2 }}>⬡</div>
            <div style={{ fontSize: 12, letterSpacing: 4 }}>PRESS START TO BEGIN JAMMU ANALYSIS</div>
          </div>
        )}

        <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
          <button onClick={() => setActiveJammuTab("main")} style={{ padding: "8px 18px", borderRadius: 4, border: `1px solid ${activeJammuTab === "main" ? C.neon : C.border}`, background: C.card, color: activeJammuTab === "main" ? C.neon : C.text, fontFamily: FONT }}>
            MAIN DATA
          </button>
          <button onClick={() => setActiveJammuTab("greeks")} style={{ padding: "8px 18px", borderRadius: 4, border: `1px solid ${activeJammuTab === "greeks" ? C.amber : C.border}`, background: C.card, color: activeJammuTab === "greeks" ? C.amber : C.text, fontFamily: FONT }}>
            GREEKS
          </button>
        </div>

        {/* ── PCR COLUMN + INDEX GRIDS ── */}
        {activeJammuTab === "main" && data && (
          <>
            {/* ── ROW 1: ITM VOLUME + OI SELLERS ── */}
            <div style={{ marginBottom: 8, fontSize: 9, color: C.neon, letterSpacing: 4, borderBottom: `1px solid ${C.border}`, paddingBottom: 6 }}>
              SECTION 1 — DAY / WEEKLY RANGE
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
              <RangePanel label="NIFTY" d={rangeData?.ranges?.nifty} color={C.blue} />
              <RangePanel label="SENSEX" d={rangeData?.ranges?.sensex} color={C.purple} />
              <RangePanel label="BANKNIFTY" d={rangeData?.ranges?.banknifty} color={C.pink} />
            </div>

            {/* ── ROW 2: FUTURES SUMMARY ── */}
            <div style={{ marginBottom: 8, fontSize: 9, color: C.neon, letterSpacing: 4, borderBottom: `1px solid ${C.border}`, paddingBottom: 6 }}>
              SECTION 2 — FUTURES SUMMARY (3 MONTHS)
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
              <FuturesSummary label="NIFTY" d={data.nifty} color={C.blue} fmtOI={fmtOI} />
              <FuturesSummary label="BANKNIFTY" d={data.banknifty} color={C.pink} fmtOI={fmtOI} />
            </div>

            {/* ── ROW 3: GREEKS STRIKES ── */}
            {false && (
            <>
              {/* ── ROW 3: GREEKS STRIKES ── */}
              <div style={{ marginBottom: 8, fontSize: 9, color: C.neon, letterSpacing: 4, borderBottom: `1px solid ${C.border}`, paddingBottom: 6 }}>
                SECTION 3 — NEAREST STRIKES (Δ≥0.40 · Γ≥0.0012 · IV≥15%)
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <GreeksPanel label="NIFTY"     d={data.nifty}     color={C.blue}   fmtN={fmtN} />
                <GreeksPanel label="SENSEX"    d={data.sensex}    color={C.purple} fmtN={fmtN} />
                <GreeksPanel label="BANKNIFTY" d={data.banknifty} color={C.pink}   fmtN={fmtN} />
                <GreeksPanel label="BANKEX"    d={data.bankex}    color={C.amber}  fmtN={fmtN} />
              </div>
            </>
          )}
          </>
        )}

        {activeJammuTab === "greeks" && (
          <div>
            <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 16 }}>
              <button onClick={startGreeks} disabled={greeksLoading} style={{ background: C.amber, color: "#000", border: "none", padding: "9px 22px", borderRadius: 4, fontWeight: "bold", fontFamily: FONT }}>
                {greeksLoading ? "FETCHING..." : "FETCH / SCAN GREEKS"}
              </button>

              <button onClick={stopGreeks} style={{ background: C.red, color: "#fff", border: "none", padding: "9px 22px", borderRadius: 4, fontWeight: "bold", fontFamily: FONT }}>
                STOP GREEKS
              </button>

              {greeksError && <span style={{ color: C.red, fontSize: 11 }}>{greeksError}</span>}
            </div>

            {greeksData && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <GreeksPanel label="NIFTY" d={greeksData.nifty} color={C.blue} fmtN={fmtN} />
                <GreeksPanel label="SENSEX" d={greeksData.sensex} color={C.purple} fmtN={fmtN} />
                <GreeksPanel label="BANKNIFTY" d={greeksData.banknifty} color={C.pink} fmtN={fmtN} />
                <GreeksPanel label="BANKEX" d={greeksData.bankex} color={C.amber} fmtN={fmtN} />
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────

function RangePanel({ label, d, color }) {
  if (!d) {
    return (
      <div style={cardStyle}>
        <div style={{ fontSize: 11, color, fontWeight: "bold", letterSpacing: 2 }}>{label}</div>
        <div style={{ color: C.muted, fontSize: 11, marginTop: 8 }}>No range data</div>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 11, color, fontWeight: "bold", letterSpacing: 2, marginBottom: 10, borderBottom: `1px solid ${C.border}`, paddingBottom: 6 }}>
        {label} RANGE
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
        <Stat label="LIVE SPOT" value={d.spot} color={C.blue} />
        <Stat label="INDIA VIX" value={d.indiaVix} color={C.amber} />
      </div>

      <div style={{ fontSize: 9, color: C.muted, letterSpacing: 2, marginBottom: 6 }}>
        RANGE: {d.rangePercent}% · {d.rangePoints} pts
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <Stat label="SUPPORT" value={d.support} color={C.green} />
        <Stat label="RESISTANCE" value={d.resistance} color={C.red} />
      </div>
    </div>
  );
}

function FuturesSummary({ label, d, color, fmtOI }) {
  if (!d || d.error || !d.futures?.length)
    return <div style={cardStyle}><div style={{ fontSize: 11, color }}>{label} FUTURES</div><div style={{ color: C.muted, fontSize: 11, marginTop: 8 }}>No futures data</div></div>;

  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 11, color, fontWeight: "bold", letterSpacing: 2, marginBottom: 10, borderBottom: `1px solid ${C.border}`, paddingBottom: 6 }}>
        {label} FUTURES SUMMARY
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
        <thead>
          <tr style={{ background: C.dim }}>
            {["EXPIRY", "LTP", "OI", "CHANGE", "VOLUME", "BIAS"].map(h => (
              <th key={h} style={{ padding: "6px 10px", textAlign: "right", color: C.muted, fontSize: 8, letterSpacing: 1, fontWeight: "normal" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {d.futures.map((f, i) => {
            const bias =
              Number(f.change || 0) > 0
                ? { label: "BULL", color: "#00ff88" }
                : Number(f.change || 0) < 0
                  ? { label: "BEAR", color: "#ff3366" }
                  : { label: "NEUT", color: "#ffaa00" };
            return (
              <tr key={i} style={{ borderTop: `1px solid ${C.border}` }}>
                <td style={{ padding: "7px 10px", color: C.text }}>{f.expiry}</td>
                <td style={{ padding: "7px 10px", textAlign: "right", color: C.blue }}>₹{Number(f.ltp || 0).toFixed(2)}</td>
                <td style={{ padding: "7px 10px", textAlign: "right", color: C.text }}>
                  {fmtOI(f.oi)}
                </td>

                <td
                  style={{
                    padding: "7px 10px",
                    textAlign: "right",
                    color: Number(f.change || 0) >= 0 ? C.green : C.red,
                  }}
                >
                  {Number(f.change || 0) >= 0 ? "+" : ""}
                  {Number(f.change || 0).toFixed(2)}
                  {" "}
                  ({Number(f.changePercent || 0).toFixed(2)}%)
                </td>

                <td style={{ padding: "7px 10px", textAlign: "right", color: C.blue }}>
                  {fmtOI(f.volume)}
                </td>
                <td style={{ padding: "7px 10px", textAlign: "right" }}>
                  <span style={{ color: bias.color, fontSize: 9, fontWeight: "bold", border: `1px solid ${bias.color}40`, padding: "1px 6px", borderRadius: 3 }}>{bias.label}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function GreeksPanel({ label, d, color, fmtN }) {
  if (!d || d.error || !d.greeks)
    return <div style={cardStyle}><div style={{ fontSize: 11, color }}>{label} GREEKS</div><div style={{ color: C.muted, fontSize: 11, marginTop: 8 }}>No data</div></div>;

  const { ce, pe } = d.greeks;

  const StrikeCard = ({ s, type }) => {
    if (!s) return <div style={{ ...cardStyle, background: C.dim, opacity: 0.4, padding: "10px 12px" }}><div style={{ color: C.muted, fontSize: 10 }}>No qualifying strike</div></div>;
    const c = type === "CE" ? "#00ff88" : "#ff3366";
    return (
      <div style={{ ...cardStyle, borderColor: c + "30", padding: "10px 12px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <span style={{ color: c, fontWeight: "bold", fontSize: 12 }}>{s.strike} {type}</span>
          <span style={{ color: C.blue, fontSize: 12 }}>₹{fmtN(s.ltp)}</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 4 }}>
          <MiniStat label="Δ DELTA" value={fmtN(s.delta, 4)} color={c} />
          <MiniStat label="Γ GAMMA" value={fmtN(s.gamma, 4)} color={c} />
          <MiniStat label="IV %" value={fmtN(s.iv, 1) + "%"} color={C.amber} />
        </div>
      </div>
    );
  };

  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 11, color, fontWeight: "bold", letterSpacing: 2, marginBottom: 10, borderBottom: `1px solid ${C.border}`, paddingBottom: 6 }}>
        {label} — QUALIFYING STRIKES
      </div>
      <div style={{ fontSize: 9, color: C.muted, letterSpacing: 2, marginBottom: 6 }}>CALL SIDE (CE)</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
        <StrikeCard s={ce?.[0]} type="CE" />
        <StrikeCard s={ce?.[1]} type="CE" />
      </div>
      <div style={{ fontSize: 9, color: C.muted, letterSpacing: 2, marginBottom: 6 }}>PUT SIDE (PE)</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <StrikeCard s={pe?.[0]} type="PE" />
        <StrikeCard s={pe?.[1]} type="PE" />
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div>
      <div style={{ fontSize: 8, color: C.muted, letterSpacing: 2, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: "bold", color }}>{value}</div>
    </div>
  );
}

function MiniStat({ label, value, color }) {
  return (
    <div style={{ background: C.dim, borderRadius: 4, padding: "4px 6px" }}>
      <div style={{ fontSize: 7, color: C.muted, letterSpacing: 1 }}>{label}</div>
      <div style={{ fontSize: 11, color, fontWeight: "bold" }}>{value}</div>
    </div>
  );
}
//ENDOFFILE
